import { formatCurrency, formatDisplayDate, formatInteger } from "../formatters";

export interface BrandDailyRow {
  date: string;
  brand: string;
  qualified: number;
  reported: number;
  paid: number;
  income: number;
  epq: number;
  epp: number;
  failure: number;
  pending: number;
  initial: number;
}

// Maps one row of the live brandsDailyId query's `query_output` array
// (see OptifundDashboardEndPointLogic.runQuery, which unwraps query_output
// before this runs) into the table/KPI/chart-ready shape.
export function mapBrandDailyApiRow(raw: any): BrandDailyRow {
  return {
    date: raw?.activity_date,
    brand: raw?.brand_name,
    qualified: raw?.qualified_leads ?? 0,
    reported: raw?.successfully_reported_leads ?? 0,
    paid: raw?.paid_leads ?? 0,
    income: raw?.total_income ?? 0,
    epq: raw?.EPQ ?? 0,
    epp: raw?.EPP ?? 0,
    failure: raw?.Failure_reported_leads ?? 0,
    pending: raw?.Pending_reported_leads ?? 0,
    initial: raw?.Initial_reported_leads ?? 0,
  };
}

// Mirrors BrandsDailyTable.tsx's TABLE_HEADERS labels/column order and cell
// formatting exactly, so the exported CSV matches what's on screen.
export const BRANDS_DAILY_CSV_HEADERS = [
  "Date",
  "Brand",
  "Qualified",
  "Reported",
  "Paid",
  "Total income",
  "EPQ",
  "EPP",
  "Failure",
  "Pending",
  "Initial",
];

export function toBrandsDailyCsvRow(row: BrandDailyRow): Array<string | number> {
  return [
    formatDisplayDate(row.date),
    row.brand,
    formatInteger(row.qualified),
    formatInteger(row.reported),
    formatInteger(row.paid),
    formatCurrency(row.income),
    formatCurrency(row.epq),
    formatCurrency(row.epp),
    formatInteger(row.failure),
    formatInteger(row.pending),
    formatInteger(row.initial),
  ];
}

export interface BrandMtdRow {
  brand: string;
  reported: number;
  paid: number;
  income: number;
  epq: number;
  epp: number;
  pending: number;
  initial: number;
}

// Maps one row of the live brandsMtdId query's `query_output` array. Note:
// the MTD query also returns Failure_reported_leads, but the MTD table
// (per the mockup's design, unlike the Daily table) doesn't show a Failure
// column, so it's intentionally left out of this shape.
export function mapBrandMtdApiRow(raw: any): BrandMtdRow {
  return {
    brand: raw?.brand_name,
    reported: raw?.successfully_reported_leads ?? 0,
    paid: raw?.paid_leads ?? 0,
    income: raw?.total_income ?? 0,
    epq: raw?.EPQ ?? 0,
    epp: raw?.EPP ?? 0,
    pending: raw?.Pending_reported_leads ?? 0,
    initial: raw?.Initial_reported_leads ?? 0,
  };
}

// Mirrors BrandsMtdTable.tsx's TABLE_HEADERS labels/column order and cell
// formatting exactly, so the exported CSV matches what's on screen.
export const BRANDS_MTD_CSV_HEADERS = [
  "Brand",
  "Reported",
  "Paid",
  "Total income",
  "EPQ",
  "EPP",
  "Pending",
  "Initial",
];

export function toBrandsMtdCsvRow(row: BrandMtdRow): Array<string | number> {
  return [
    row.brand,
    formatInteger(row.reported),
    formatInteger(row.paid),
    formatCurrency(row.income),
    formatCurrency(row.epq),
    formatCurrency(row.epp),
    formatInteger(row.pending),
    formatInteger(row.initial),
  ];
}
